import { idToken } from '../firebase';
import { BACKEND } from './posts';
import { crewAnswered } from './crew';
import { loadProfile, saveProfile } from './state';
import type { StoryCaption, Segment } from './storyboard-play';
export * from './storyboard-play';

/**
 * The crew's storyboard of a told story (well/research/16, C1). Day 1's story
 * is kept the moment the warm-up ends, so the crew develop it while you do
 * the exercise; it comes back as a print of its own, next to the exercise's.
 * The backend holds it privately (kindling-storyboard.service.ts) and hands it
 * only to the person who told it.
 */
export type StoryboardStatus = 'waiting' | 'transcribing' | 'planning' | 'drawing' | 'done' | 'failed' | 'too-short';
export interface Storyboard {
  storyId: string;
  postId?: string;
  day: number;
  challengeId: string;
  status: StoryboardStatus;
  source: 'warmup' | 'audio';
  subject?: string;
  error?: string;
  blocked?: 'billing';
  title?: string;
  panels?: StoryCaption[];
  ending?: StoryCaption;
  pageUrl?: string;
  /** the narrator's reading, and where each line sits in it */
  narration?: { url: string; segments: Segment[]; seconds: number };
  transcript?: string;
  duration?: number;
  /** recast (well/product/32): `pageUrl` is then the page with you in it, and this the one it replaced */
  beforePageUrl?: string;
  cast?: StoryCast;
  /** "Want to be in it?" may be asked of this one (the backend decides: Day 1's story, crew on, nobody drawn yet) */
  castable?: boolean;
}

export type CastStatus = 'named' | 'casting' | 'cast' | 'failed';
export interface StoryCast { status: CastStatus; name?: string; meUrl?: string; canRedraw: boolean; blocked?: 'billing' }

export const WORKING: StoryboardStatus[] = ['waiting', 'transcribing', 'planning', 'drawing'];

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await idToken();
  return fetch(`${BACKEND}${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...(init.headers || {}) } });
}

/** Keep the story the moment it is told; the crew start on it if they may. */
export async function keepStory(blob: Blob, meta: { day: number; seconds: number; subject?: string }): Promise<{ storyId: string; status: StoryboardStatus }> {
  const form = new FormData();
  form.append('meta', JSON.stringify(meta));
  form.append('audio', blob, blob.type.includes('mp4') ? 'story.m4a' : 'story.webm');
  // After the crew's answer, never beside it: a story that lands first is parked
  // as waiting, and the answer's request may already have looked for it.
  await crewAnswered();
  const res = await call('/api/kindling/stories', { method: 'POST', body: form });
  if (!res.ok) throw new Error(`story: ${res.status}`);
  return res.json();
}

/** null: nothing storyboarded (yet). `id` is a story id, or a post id to find the story told with it. */
export async function getStoryboard(id: string): Promise<Storyboard | null> {
  const res = await call(`/api/kindling/stories/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw Object.assign(new Error(`storyboard: ${res.status}`), { status: res.status });
  const sb = await res.json() as Storyboard;
  noteCast(sb);
  return sb;
}

/** Cast, wherever it was seen: the profile remembers there is a "me" (well/product/32 §5). */
export function noteCast(sb: Pick<Storyboard, 'cast'>): void {
  const me = sb.cast?.status === 'cast' ? sb.cast.meUrl : undefined;
  if (!me) return;
  const p = loadProfile();
  if (p.castMe !== me || (sb.cast?.name && !p.name)) saveProfile({ ...p, castMe: me, name: p.name || sb.cast?.name });
}

/** Make it now, or (`again`) make it over. Throws with `code` 'crew_off' or 'no_story' when it cannot. */
export async function askForStoryboard(id: string, again = false): Promise<Storyboard> {
  const res = await call(`/api/kindling/stories/${encodeURIComponent(id)}/redo`, { method: 'POST', body: JSON.stringify({ force: again }) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || `${res.status}`), { code: body.code, status: res.status });
  return body;
}

// ── held until you turn it over, like every other print (lib/reveal.ts) ─────

const OPENED = 'kindling.storiesOpened';
function openedStories(): string[] {
  try { return JSON.parse(localStorage.getItem(OPENED) || '[]') as string[]; } catch { return []; }
}
export function storyOpened(id: string): boolean { return openedStories().includes(id); }
export function markStoryOpened(id: string): void {
  try { localStorage.setItem(OPENED, JSON.stringify([...new Set([...openedStories(), id])].slice(-100))); } catch { /* private mode */ }
}

/** Library beds (made once, public/audio/beds): the same story always gets the same one. */
export const BEDS = ['/audio/beds/storybook.mp3', '/audio/beds/pizzicato.mp3', '/audio/beds/lullaby.mp3'];
export function bedFor(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return BEDS[h % BEDS.length];
}

/** The words on the story's print: its title once there is one, else what it was about. */
export function storyWords(sb: Pick<Storyboard, 'title' | 'subject'>): string {
  return sb.title || (sb.subject ? `A story about ${sb.subject}` : 'Your story');
}
